import {
  CustomFormattedMessage,
  TranslationsKeys,
  useCustomIntl,
} from '../../../../i18n/i18n';
import Multiselect, {
  MultiselectOption,
} from '@rio-cloud/rio-uikit/Multiselect';
import Select, { SelectOption } from '@rio-cloud/rio-uikit/Select';
import TableSearch from '@rio-cloud/rio-uikit/TableSearch';
import TableToolbar from '@rio-cloud/rio-uikit/TableToolbar';
import { UserManualLink } from '../../../../components/dialog/userManualDialog/UserManualLink';
import { userManualDialogChapters } from '../../../../state/dialogs/chapters';
import { CardExpirationFilter, ServiceFilterType } from './VehicleTable';
import { ServiceType } from '../../../../codegen/vehicles';

type VehicleTableToolbarProps = {
  groupFilterOptions: MultiselectOption[];
  onGroupFilterChanged: (selectedOptions: string[]) => void;
  serviceFilterOptions: MultiselectOption[];
  onServiceFilterChanged: (
    selectedFilters: (ServiceType | ServiceFilterType)[],
  ) => void;
  cardExpirationFilterOptions: SelectOption[];
  onCardExpirationFilterChanged: (
    selected: CardExpirationFilter | undefined,
  ) => void;
  searchBarValue: string;
  onSearchBarValueChanged: (value: string) => void;
};

export const VehicleTableToolbar = ({
  groupFilterOptions,
  onGroupFilterChanged,
  serviceFilterOptions,
  onServiceFilterChanged,
  cardExpirationFilterOptions,
  onCardExpirationFilterChanged,
  searchBarValue,
  onSearchBarValueChanged,
}: VehicleTableToolbarProps) => {
  const intl = useCustomIntl();
  const getLabel = (id: TranslationsKeys) => {
    return intl.formatMessage({
      id,
    });
  };

  return (
    <TableToolbar>
      <div
        className="table-toolbar-container"
        data-testid={'onboarding-table-toolbar'}
      >
        <div className="table-toolbar-group-left">
          <div className="table-toolbar-column">
            <div className="btn-toolbar table-btn-toolbar">
              <div data-testid="onboarding-vehicle-table-group-filter">
                <label>
                  <CustomFormattedMessage id={'common.vehicle.groups'} />
                </label>
                <Multiselect
                  placeholder={getLabel(
                    'onboarding.vehicleTable.toolbar.vehicleGroupFilterPlaceholder',
                  )}
                  options={groupFilterOptions}
                  useFilter
                  onChange={onGroupFilterChanged}
                />
              </div>
              <div data-testid="onboarding-vehicle-table-service-filter">
                <label>
                  <CustomFormattedMessage id={'common.services'} />
                </label>
                <Multiselect
                  useClear
                  className={'width-200'}
                  placeholder={getLabel(
                    'common.filter.serviceFilter.placeholder',
                  )}
                  options={serviceFilterOptions}
                  onChange={(selected: string[]) =>
                    onServiceFilterChanged(
                      selected as (ServiceType | ServiceFilterType)[],
                    )
                  }
                />
              </div>
              <div data-testid="onboarding-vehicle-table-card-expiration-filter">
                <label>
                  <CustomFormattedMessage
                    id={'onboarding.vehicleTable.toolbar.cardExpirationFilter'}
                  />
                </label>
                <Select
                  useClear
                  className={'width-200'}
                  placeholder={getLabel(
                    'onboarding.vehicleTable.toolbar.cardExpirationFilterPlaceholder',
                  )}
                  options={cardExpirationFilterOptions}
                  onChange={(option) =>
                    onCardExpirationFilterChanged(
                      option?.id as CardExpirationFilter | undefined,
                    )
                  }
                />
              </div>
              <div className={'align-content-end'}>
                <UserManualLink
                  chapter={userManualDialogChapters.vehicleOverview}
                  className="margin-10"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="table-toolbar-group-right">
          <div className="table-toolbar-column">
            <TableSearch
              data-testid={'onboarding-vehicle-table-search-bar'}
              value={searchBarValue}
              onChange={onSearchBarValueChanged}
              placeholder={getLabel(
                'onboarding.vehicleTable.toolbar.searchBarPlaceholder',
              )}
            />
          </div>
        </div>
      </div>
    </TableToolbar>
  );
};
